import type { ModuleManifest } from "./manifest-registry";
import type { SlotDefinition } from "./slot-definition.type";

export type ManifestsResult = { ok: true } | { ok: false; problems: string[] };

/** Checks the manifests against the slots hosts declare: every id is unique, every `needs` names a module that exists, nobody needs itself, a module that is always on never needs one that can be switched off, every contribution goes to a slot that exists (whose host exists) and no contribution id repeats within a slot. Returns every problem found. */
export function verifyManifests(manifests: readonly ModuleManifest[], slots: readonly SlotDefinition[]): ManifestsResult {
  const ids = manifests.map((m) => m.id);
  const byId = new Map(manifests.map((m) => [m.id, m]));
  const seen = new Set<string>();
  const problems: string[] = [];
  for (const slot of slots) if (!byId.has(slot.host)) problems.push(`unknown-host:${slot.id}:${slot.host}`);
  for (const m of manifests) {
    if (ids.indexOf(m.id) !== ids.lastIndexOf(m.id)) problems.push(`duplicate-id:${m.id}`);
    for (const need of m.needs) {
      if (need === m.id) problems.push(`needs-itself:${m.id}`);
      else if (!byId.has(need)) problems.push(`unknown-need:${m.id}:${need}`);
      else if (!m.optional && byId.get(need)?.optional) problems.push(`core-needs-optional:${m.id}:${need}`);
    }
    for (const c of m.contributes) {
      if (!slots.some((s) => s.id === c.slot)) problems.push(`unknown-slot:${m.id}:${c.slot}`);
      if (seen.has(`${c.slot}/${c.id}`)) problems.push(`duplicate-contribution:${c.slot}/${c.id}`);
      seen.add(`${c.slot}/${c.id}`);
    }
  }
  return problems.length ? { ok: false, problems: [...new Set(problems)] } : { ok: true };
}

export type SwitchResult = { ok: true } | { ok: false; code: "module-unknown" | "module-always-on" | "module-needs-off" | "module-needed-by-on"; ids: string[] };

/** The modules that are really on: every module that is always on, plus each optional one that was switched on and whose needs are all really on too (a stored "on" does nothing while something it needs is off). */
export function effectiveModules(manifests: readonly ModuleManifest[], switchedOn: ReadonlySet<string>): Set<string> {
  const on = new Set(manifests.filter((m) => !m.optional).map((m) => m.id));
  for (let changed = true; changed; ) {
    changed = false;
    for (const m of manifests) {
      if (on.has(m.id) || !switchedOn.has(m.id) || !m.needs.every((n) => on.has(n))) continue;
      on.add(m.id);
      changed = true;
    }
  }
  return on;
}

/** The modules that need `id` directly (so `id` cannot go off while any of them is on). */
export function dependentsOf(manifests: readonly ModuleManifest[], id: string): ModuleManifest[] {
  return manifests.filter((m) => m.needs.includes(id));
}

/** Whether `id` may go on or off now. Always-on modules and unknown ones refuse; a module cannot go on while a module it needs is off (`ids` lists those), and cannot go off while a module that needs it is on (`ids` lists those). `switchedOn` is what is stored. */
export function verifySwitch(manifests: readonly ModuleManifest[], switchedOn: ReadonlySet<string>, id: string, turnOn: boolean): SwitchResult {
  const manifest = manifests.find((m) => m.id === id);
  if (!manifest) return { ok: false, code: "module-unknown", ids: [id] };
  if (!manifest.optional) return { ok: false, code: "module-always-on", ids: [id] };
  const on = effectiveModules(manifests, switchedOn);
  if (turnOn) {
    const off = manifest.needs.filter((n) => !on.has(n));
    return off.length ? { ok: false, code: "module-needs-off", ids: off } : { ok: true };
  }
  const blocking = dependentsOf(manifests, id).filter((m) => on.has(m.id)).map((m) => m.id);
  return blocking.length ? { ok: false, code: "module-needed-by-on", ids: blocking } : { ok: true };
}
